import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ConnectorListResponseSchema,
  type CreateSourceRequest,
  CreateSourceResponseSchema,
  LookupResponseSchema,
  SourceSchema,
  type UpdateSourceRequest,
} from '@vision360/contracts';
import { useApi } from '@/lib/auth/session-context';
import { SOURCES_KEY } from '@/widgets/_sdk/hooks';

/** Source editor over the API (TechSpec §6.1; step 36a, PRD VS-025). */

export const CONNECTORS_KEY = ['connectors'] as const;
export const LOOKUP_LIMIT = 20;

export function useConnectors() {
  const api = useApi();
  return useQuery({
    queryKey: CONNECTORS_KEY,
    queryFn: ({ signal }) =>
      api.request('/api/v1/connectors', { schema: ConnectorListResponseSchema, signal }),
    // Connectors only change with a deploy.
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useLookup(connectorId: string, name: string, q: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: [...CONNECTORS_KEY, connectorId, 'lookups', name, q],
    queryFn: ({ signal }) => {
      const query = new URLSearchParams({ q, limit: String(LOOKUP_LIMIT) });
      return api.request(
        `/api/v1/connectors/${encodeURIComponent(connectorId)}/lookups/${encodeURIComponent(name)}?${query}`,
        { schema: LookupResponseSchema, signal },
      );
    },
    enabled,
    staleTime: 5 * 60_000,
    // The previous results stay listed while the next search runs: no flicker per keystroke.
    placeholderData: keepPreviousData,
  });
}

function useSourceMutation<V, R>(send: (api: ReturnType<typeof useApi>, vars: V) => Promise<R>) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: V) => send(api, vars),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: SOURCES_KEY }),
  });
}

export const useCreateSource = () =>
  useSourceMutation((api, body: CreateSourceRequest) =>
    api.request('/api/v1/sources', { method: 'POST', body, schema: CreateSourceResponseSchema }),
  );

export const useUpdateSource = () =>
  useSourceMutation((api, { id, patch }: { id: string; patch: UpdateSourceRequest }) =>
    api.request(`/api/v1/sources/${id}`, { method: 'PATCH', body: patch, schema: SourceSchema }),
  );
